import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, ImagePlus, Store, Loader2 } from 'lucide-react';

import { getMe, updateMe, type SupplierProfile } from '@/services/profile';
import { uploadImage } from '@/services/catalog';
import { useAuthStore } from '@/store/auth.store';
import { PageHeader, Spinner, SectionCard, Field, Select, Textarea } from '@/components/fields';

interface FormState {
  name: string;
  phone: string;
  description: string;
  city: string;
  logoUrl: string;
  legalForm: string;
  legalName: string;
  inn: string;
  kpp: string;
  ogrn: string;
  taxSystem: string;
  legalAddress: string;
  warehouseAddress: string;
  contactPerson: string;
  bankAccount: string;
  bik: string;
  bankName: string;
  corrAccount: string;
  tags: string;
  deliveryRegions: string;
  deliverySchedule: string;
  minOrderRub: string;
  freeFromRub: string;
  defaultPaymentTerms: string;
  defaultDeferDays: string;
}

const rub = (v: number | null) => (v != null ? String(Math.round(v / 100)) : '');

function toForm(p: SupplierProfile): FormState {
  return {
    name: p.name ?? '',
    phone: p.phone ?? '',
    description: p.description ?? '',
    city: p.city ?? '',
    logoUrl: p.logoUrl ?? '',
    legalForm: p.legalForm ?? '',
    legalName: p.legalName ?? '',
    inn: p.inn ?? '',
    kpp: p.kpp ?? '',
    ogrn: p.ogrn ?? '',
    taxSystem: p.taxSystem ?? '',
    legalAddress: p.legalAddress ?? '',
    warehouseAddress: p.warehouseAddress ?? '',
    contactPerson: p.contactPerson ?? '',
    bankAccount: p.bankAccount ?? '',
    bik: p.bik ?? '',
    bankName: p.bankName ?? '',
    corrAccount: p.corrAccount ?? '',
    tags: (p.tags ?? []).join(', '),
    deliveryRegions: (p.deliveryRegions ?? []).join(', '),
    deliverySchedule: p.deliverySchedule ?? '',
    minOrderRub: rub(p.minOrderAmount),
    freeFromRub: rub(p.freeDeliveryFrom),
    defaultPaymentTerms: p.defaultPaymentTerms ?? 'PREPAY',
    defaultDeferDays: p.defaultDeferDays != null ? String(p.defaultDeferDays) : '',
  };
}

const txt = (v: string) => (v.trim() ? v.trim() : null);
const csv = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);
const rubToKop = (v: string) => (v.trim() === '' ? null : Math.round(Number(v) * 100));
const intOrNull = (v: string) => (v.trim() === '' ? null : Math.round(Number(v)));

function toPatch(f: FormState): Partial<SupplierProfile> {
  return {
    name: f.name.trim(),
    phone: txt(f.phone),
    description: txt(f.description),
    city: txt(f.city),
    logoUrl: txt(f.logoUrl),
    legalForm: (f.legalForm || null) as SupplierProfile['legalForm'],
    legalName: txt(f.legalName),
    inn: txt(f.inn),
    kpp: txt(f.kpp),
    ogrn: txt(f.ogrn),
    taxSystem: (f.taxSystem || null) as SupplierProfile['taxSystem'],
    legalAddress: txt(f.legalAddress),
    warehouseAddress: txt(f.warehouseAddress),
    contactPerson: txt(f.contactPerson),
    bankAccount: txt(f.bankAccount),
    bik: txt(f.bik),
    bankName: txt(f.bankName),
    corrAccount: txt(f.corrAccount),
    tags: csv(f.tags),
    deliveryRegions: csv(f.deliveryRegions),
    deliverySchedule: txt(f.deliverySchedule),
    minOrderAmount: rubToKop(f.minOrderRub),
    freeDeliveryFrom: rubToKop(f.freeFromRub),
    defaultPaymentTerms: f.defaultPaymentTerms as SupplierProfile['defaultPaymentTerms'],
    defaultDeferDays: intOrNull(f.defaultDeferDays),
  };
}

export default function Profile() {
  const setSupplier = useAuthStore((s) => s.setSupplier);
  const initial = useAuthStore((s) => s.supplier);

  const [form, setForm] = useState<FormState | null>(initial ? toForm(initial) : null);
  const [email, setEmail] = useState(initial?.email ?? '');
  const [loading, setLoading] = useState(!initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let alive = true;
    getMe()
      .then((p) => {
        if (!alive) return;
        setSupplier(p);
        setForm(toForm(p));
        setEmail(p.email);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: keyof FormState) => (v: string) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
    setSaved(false);
  };

  const onPickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const { url } = await uploadImage(file, 'logo');
      set('logoUrl')(url);
    } catch {
      /* тост */
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    try {
      setSaving(true);
      const p = await updateMe(toPatch(form));
      setSupplier(p);
      setForm(toForm(p));
      setSaved(true);
      toast.success('Профиль сохранён');
    } catch {
      /* тост */
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <Spinner label="Загружаем профиль…" />;

  return (
    <div>
      <PageHeader title="Профиль" subtitle="Данные компании, реквизиты и условия работы" />

      <form onSubmit={onSubmit} className="space-y-4 pb-24">
        <SectionCard title="Основное" description="Как вас увидят заведения в каталоге «Закуп»">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-20 h-20 rounded-xl bg-paper border border-line overflow-hidden flex items-center justify-center shrink-0">
              {uploading ? (
                <Loader2 className="animate-spin text-ink/40" size={20} />
              ) : form.logoUrl ? (
                <img src={form.logoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <Store size={24} className="text-ink/25" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="btn-outline cursor-pointer !py-1.5 text-sm">
                <ImagePlus size={15} /> {form.logoUrl ? 'Заменить логотип' : 'Загрузить логотип'}
                <input type="file" accept="image/*" className="hidden" onChange={onPickLogo} />
              </label>
              {form.logoUrl && (
                <button
                  type="button"
                  onClick={() => set('logoUrl')('')}
                  className="text-xs text-ink/40 hover:text-red-600 text-left"
                >
                  Удалить логотип
                </button>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Название / бренд" required>
              <input className="input" value={form.name} onChange={(e) => set('name')(e.target.value)} />
            </Field>
            <Field label="Телефон">
              <input className="input" value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="+7 999 123-45-67" />
            </Field>
            <Field label="Город">
              <input className="input" value={form.city} onChange={(e) => set('city')(e.target.value)} placeholder="Москва" />
            </Field>
            <Field label="Email (логин)">
              <input className="input bg-paper text-ink/50" value={email} disabled />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Описание">
                <Textarea value={form.description} onChange={(e) => set('description')(e.target.value)} placeholder="Чем занимаетесь, ассортимент, преимущества…" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Категории / теги" hint="через запятую">
                <input className="input" value={form.tags} onChange={(e) => set('tags')(e.target.value)} placeholder="овощи, фрукты, зелень" />
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Юридические реквизиты" description="Нужны для официальной работы с ресторанами">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Форма">
              <Select value={form.legalForm} onChange={(e) => set('legalForm')(e.target.value)}>
                <option value="">— не указано —</option>
                <option value="OOO">ООО</option>
                <option value="IP">ИП</option>
                <option value="SAMOZANYATY">Самозанятый (НПД)</option>
                <option value="AO">АО</option>
              </Select>
            </Field>
            <Field label="Налогообложение">
              <Select value={form.taxSystem} onChange={(e) => set('taxSystem')(e.target.value)}>
                <option value="">— не указано —</option>
                <option value="OSN">ОСН (с НДС)</option>
                <option value="USN">УСН (без НДС)</option>
                <option value="NPD">НПД (самозанятый)</option>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Юридическое наименование">
                <input className="input" value={form.legalName} onChange={(e) => set('legalName')(e.target.value)} placeholder="ООО «Свежесть»" />
              </Field>
            </div>
            <Field label="ИНН">
              <input className="input" inputMode="numeric" value={form.inn} onChange={(e) => set('inn')(e.target.value)} placeholder="10 или 12 цифр" />
            </Field>
            <Field label="КПП">
              <input className="input" inputMode="numeric" value={form.kpp} onChange={(e) => set('kpp')(e.target.value)} placeholder="9 цифр" />
            </Field>
            <Field label="ОГРН / ОГРНИП">
              <input className="input" inputMode="numeric" value={form.ogrn} onChange={(e) => set('ogrn')(e.target.value)} placeholder="13 или 15 цифр" />
            </Field>
            <Field label="Контактное лицо">
              <input className="input" value={form.contactPerson} onChange={(e) => set('contactPerson')(e.target.value)} placeholder="Иванов И.И." />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Юридический адрес">
                <input className="input" value={form.legalAddress} onChange={(e) => set('legalAddress')(e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Адрес склада">
                <input className="input" value={form.warehouseAddress} onChange={(e) => set('warehouseAddress')(e.target.value)} />
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Банковские реквизиты">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Расчётный счёт">
              <input className="input" inputMode="numeric" value={form.bankAccount} onChange={(e) => set('bankAccount')(e.target.value)} placeholder="20 цифр" />
            </Field>
            <Field label="БИК">
              <input className="input" inputMode="numeric" value={form.bik} onChange={(e) => set('bik')(e.target.value)} placeholder="9 цифр" />
            </Field>
            <Field label="Банк">
              <input className="input" value={form.bankName} onChange={(e) => set('bankName')(e.target.value)} placeholder="ПАО Сбербанк" />
            </Field>
            <Field label="Корр. счёт">
              <input className="input" inputMode="numeric" value={form.corrAccount} onChange={(e) => set('corrAccount')(e.target.value)} placeholder="20 цифр" />
            </Field>
          </div>
        </SectionCard>

        <SectionCard title="Доставка и условия">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Регионы доставки" hint="через запятую">
                <input className="input" value={form.deliveryRegions} onChange={(e) => set('deliveryRegions')(e.target.value)} placeholder="Москва, Московская область" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="График доставки">
                <input className="input" value={form.deliverySchedule} onChange={(e) => set('deliverySchedule')(e.target.value)} placeholder="Пн–Сб, заказ до 18:00" />
              </Field>
            </div>
            <Field label="Минимальный заказ, ₽">
              <input className="input" inputMode="numeric" value={form.minOrderRub} onChange={(e) => set('minOrderRub')(e.target.value)} placeholder="5000" />
            </Field>
            <Field label="Бесплатная доставка от, ₽">
              <input className="input" inputMode="numeric" value={form.freeFromRub} onChange={(e) => set('freeFromRub')(e.target.value)} placeholder="15000" />
            </Field>
            <Field label="Оплата по умолчанию">
              <Select value={form.defaultPaymentTerms} onChange={(e) => set('defaultPaymentTerms')(e.target.value)}>
                <option value="PREPAY">Предоплата</option>
                <option value="ON_DELIVERY">Оплата при доставке</option>
                <option value="DEFERRED">Отсрочка платежа</option>
              </Select>
            </Field>
            <Field label="Отсрочка, дней">
              <input className="input" inputMode="numeric" value={form.defaultDeferDays} onChange={(e) => set('defaultDeferDays')(e.target.value)} placeholder="14" />
            </Field>
          </div>
        </SectionCard>

        <div className="fixed bottom-0 inset-x-0 lg:left-64 border-t border-line bg-white/90 backdrop-blur px-4 sm:px-6 py-3 z-20">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
            <span className="text-sm text-ink/55">
              {saved ? (
                <span className="inline-flex items-center gap-1.5 text-emerald-600">
                  <Check size={15} strokeWidth={2.4} /> Сохранено
                </span>
              ) : (
                'Изменения применятся сразу после сохранения'
              )}
            </span>
            <button type="submit" disabled={saving} className="btn-primary px-6">
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
